// SearchPage — the personal index (BUILD-BRIEF-R2 §13, the `search` agent).
//
// Mast → five-cell rail (INDEXED QUERIES · RESULTS · FROM INDEX 30D · LIVE 30D · EMBEDDED)
// → instr-band with the search box and its two verbs → Results → Recent queries.
//
// THE THREE HONESTIES THIS PAGE OWES THE OPERATOR:
//
//   1. WHERE THE ANSWER CAME FROM. Every answer says index / index_near / provider /
//      unavailable in words, and every row carries a `.tag--ghost` reading "from index" or
//      "live". An answer that cost money must not look like an answer that cost nothing.
//   2. WHAT NOBODY COUNTED. The EMBEDDED cell reads `fv--attend` "model unreachable" when the
//      embedding endpoint is down — not zero, which would claim we embedded nothing on
//      purpose. A counted zero elsewhere reads `fv--nil`.
//   3. WHY THERE IS NOTHING. "No provider answered" and "the web holds nothing" are different
//      facts and are printed differently: the first names the reason and offers what the index
//      already owns, the second says the index is empty.
//
// SEARCH versus SEARCH LIVE. The accent verb searches the index first and only reaches a
// provider on a miss; "Search live" always pays. Two verbs because they spend differently, and
// the operator is the one who decides to spend.
import { useCallback, useEffect, useState } from 'react';
import EmptyState from '../components/EmptyState.js';
import { Att, Fig, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Tbl, Tag, Zone, ZoneHead } from '../components/PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { useDeepLink, useRouteParams } from '../routes.js';
import { recentSearches, search as runSearch, searchStats } from '../api/search.js';
import type { Search, SearchResponse, SearchResult, SearchStats, ServedFrom } from '../../shared/types.js';
import './SearchPage.de.js';
import './SearchPage.fr.js';

/** The one sentence that says where an answer came from. Never a colour, never an icon alone. */
const SERVED_FROM_WORDS: Record<ServedFrom, string> = {
  index: 'answered from the index · no provider was asked',
  index_near: 'answered from the nearest query the index already held',
  provider: 'answered live by a provider, through the scout',
  unavailable: 'no provider answered',
};

export default function SearchPage() {
  const t = useT();
  const { fmtDate, fmtDateTime, fmtNum } = useLocale();
  const params = useRouteParams();

  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState<SearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stats, setStats] = useState<SearchStats | null>(null);
  const [recent, setRecent] = useState<Search[] | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  const loadSide = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([searchStats(), recentSearches(20)]);
      setStats(s);
      setRecent(r.items);
      setStatsError(null);
    } catch (err) {
      setStatsError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => { void loadSide(); }, [loadSide]);

  const run = useCallback(async (raw: string, fresh: boolean) => {
    const q = raw.trim();
    if (!q) return;
    setSearching(true);
    setError(null);
    try {
      const res = await runSearch(q, fresh);
      setAnswer(res);
    } catch (err) {
      setAnswer(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSearching(false);
      void loadSide();
    }
  }, [loadSide]);

  // `#search?q=…` is an arrival intent: run it once, then the page owns the box.
  useDeepLink(params, { q: (value) => { setQuery(value); void run(value, false); } });

  const rail = railCells(stats, t, fmtNum);

  return (
    <PageShell>
      <Mast
        kicker={t('Personal index')}
        title={t('Search')}
        sub={
          stats
            ? <>
                <Fig>{fmtNum(stats.indexed_queries)}</Fig> {t('queries')}
                <Sep />
                <Fig>{fmtNum(stats.results)}</Fig> {t('results')}
                <Sep />
                {t('kept from our own SearXNG and our own snapshots')}
              </>
            : statsError ?? t('Loading…')
        }
      />

      <Rail after={
        <div className="instr-band">
          <div className="search-wrapper" style={{ flex: '1 1 auto' }}>
            <input
              className="search-input"
              style={{ width: '100%' }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void run(query, false); }}
              placeholder={t('A name, an organisation, a question…')}
              aria-label={t('Search the index')}
            />
          </div>
          <button
            type="button"
            className="btn btn-accent ml-auto"
            disabled={searching || !query.trim()}
            onClick={() => void run(query, false)}
          >
            {searching ? t('Searching…') : t('Search the index')}
          </button>
          <button
            type="button"
            className="btn"
            disabled={searching || !query.trim()}
            title={t('Skip the index and ask a provider. This one costs.')}
            onClick={() => void run(query, true)}
          >
            {t('Search live')}
          </button>
        </div>
      }>
        {rail}
      </Rail>

      <Zone>
        <ZoneHead
          label={t('Results')}
          count={answer ? fmtNum(answer.results.length) : undefined}
          note={answer ? t(SERVED_FROM_WORDS[answer.served_from]) : undefined}
          endNote={answer?.ttl_until && answer.served_from !== 'unavailable'
            ? `${t('kept until')} ${fmtDate(answer.ttl_until)}`
            : undefined}
        />
        {error ? (
          <EmptyState kind="error" title={t('Could not search')} detail={error} onRetry={() => void run(query, false)} />
        ) : !answer ? (
          <Sheet pad>
            <p className="zone-empty">
              {t('Nothing asked yet. The index answers a repeated question for nothing, a paraphrase from the nearest question it already holds, and anything else through the scout — the one process with egress.')}
            </p>
          </Sheet>
        ) : (
          <>
            {answer.unavailable_reason && (
              <Sheet pad>
                <p className="zone-empty">
                  <strong>{t('No provider answered.')}</strong>{' '}
                  <span className="voice-system">{answer.unavailable_reason}</span>{' '}
                  {answer.results.length
                    ? t('What follows is the nearest thing the index already holds, not a live answer.')
                    : t('The index holds nothing near this query either, so there is nothing to show.')}
                </p>
              </Sheet>
            )}
            {answer.results.length > 0 && (
              <Sheet clip>
                {answer.results.map((r) => (
                  <ResultRow
                    key={r.id}
                    r={r}
                    live={answer.served_from === 'provider' && r.times_seen <= 1}
                  />
                ))}
              </Sheet>
            )}
            {answer.results.length === 0 && !answer.unavailable_reason && (
              <Sheet pad>
                <p className="zone-empty">{t('The provider answered, and answered with nothing.')}</p>
              </Sheet>
            )}
          </>
        )}
      </Zone>

      <Zone>
        <ZoneHead
          label={t('Recent queries')}
          count={recent ? fmtNum(recent.length) : undefined}
          note={t('every question anyone asked, and how it was answered')}
        />
        {statsError ? (
          <EmptyState kind="error" title={t('Could not load')} detail={statsError} onRetry={loadSide} />
        ) : !recent || recent.length === 0 ? (
          <Sheet pad>
            <p className="zone-empty">{t('No query has been recorded yet.')}</p>
          </Sheet>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th>{t('Query')}</th>
                  <th>{t('Purpose')}</th>
                  <th>{t('Answered')}</th>
                  <th>{t('Asked by')}</th>
                  <th className="num">{t('Results')}</th>
                  <th className="num">{t('Cost')}</th>
                  <th className="num">{t('When')}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((s) => (
                  <tr key={s.id}>
                    <td>{s.query_raw}</td>
                    <td>{s.purpose ?? '—'}</td>
                    <td>
                      <Tag tone={s.served_from === 'unavailable' ? 'broken' : s.served_from === 'provider' ? 'attend' : 'ghost'}>
                        {t(s.served_from ?? 'unknown')}
                      </Tag>
                    </td>
                    <td><Ref>{s.requested_by ?? '—'}</Ref></td>
                    <td className="num"><Fig>{fmtNum(s.result_count ?? 0)}</Fig></td>
                    <td className="num"><Fig>{fmtNum(s.cost_units)}</Fig></td>
                    <td className="num"><Fig>{fmtDateTime(s.created_at)}</Fig></td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>
    </PageShell>
  );
}

/**
 * The five cells. EMBEDDED is the one that must not lie: when the model is unreachable it says
 * so in `fv--attend` rather than showing the zero it would otherwise be reporting as a fact.
 */
function railCells(
  stats: SearchStats | null,
  t: (key: string, vars?: Record<string, string | number>) => string,
  fmtNum: (n: number | string | null | undefined) => string,
) {
  const dash = '—';
  const fromIndex = stats ? stats.served_from_30d.index + stats.served_from_30d.index_near : null;
  const live = stats ? stats.served_from_30d.provider : null;
  const unanswered = stats ? stats.served_from_30d.unavailable : 0;

  const embedded = (() => {
    if (!stats) return { value: dash, tone: undefined as undefined | 'unknown' | 'attend' | 'nil', detail: ' ' };
    if (stats.embedding_ok === false) {
      return {
        value: t('model unreachable'),
        tone: 'attend' as const,
        detail: stats.embedding_error ?? t('the embedding endpoint did not answer'),
      };
    }
    if (stats.embedded === null) {
      return { value: t('not collected'), tone: 'unknown' as const, detail: t('nothing has ever been embedded') };
    }
    return {
      value: fmtNum(stats.embedded),
      tone: stats.embedded === 0 ? ('nil' as const) : undefined,
      detail: t('of {n} embeddable results', { n: fmtNum(stats.embedding_total ?? 0) }),
    };
  })();

  return (
    <>
      <RailCell
        label={t('Indexed queries')}
        value={stats ? fmtNum(stats.indexed_queries) : dash}
        tone={stats && stats.indexed_queries === 0 ? 'nil' : undefined}
        detail={t('distinct questions the index has answered')}
      />
      <RailCell
        label={t('Results')}
        value={stats ? fmtNum(stats.results) : dash}
        tone={stats && stats.results === 0 ? 'nil' : undefined}
        detail={stats ? t('across {n} domains', { n: fmtNum(stats.domains) }) : ' '}
      />
      <RailCell
        label={t('From index · 30d')}
        value={fromIndex === null ? dash : fmtNum(fromIndex)}
        tone={fromIndex === 0 ? 'nil' : undefined}
        detail={t('answered without paying a provider')}
      />
      <RailCell
        label={t('Live · 30d')}
        value={live === null ? dash : fmtNum(live)}
        tone={live === 0 ? 'nil' : undefined}
        detail={stats
          ? `${t('{n} provider units', { n: fmtNum(stats.cost_30d) })} · ${t('{n} unanswered', { n: fmtNum(unanswered) })}`
          : ' '}
      />
      <RailCell label={t('Embedded')} value={embedded.value} tone={embedded.tone} detail={embedded.detail} />
    </>
  );
}

function ResultRow({ r, live }: { r: SearchResult; live: boolean }) {
  const t = useT();
  const { fmtDate } = useLocale();
  return (
    <div className="row" style={{ gridTemplateColumns: '1fr 104px 104px 96px' }}>
      <span>
        {/* An external destination is a real anchor: right-click and open-in-new-tab must work. */}
        <a className="lnk" href={r.url} target="_blank" rel="noreferrer noopener">{r.title || r.url}</a>
        <span className="d"> · <Ref>{r.domain ?? r.url_norm}</Ref></span>
        {r.snippet && (
          <>
            <br />
            {/* The provider's own words, unedited: a quote, and marked as one. */}
            <Att kind="quoted" />
            <span className="voice-record"> {r.snippet}</span>
          </>
        )}
      </span>
      <span className="d">
        <span className="k">{t('first seen')}</span>{' '}
        <Fig>{fmtDate(r.first_seen)}</Fig>
      </span>
      <span className="d">
        <span className="k">{t('last seen')}</span>{' '}
        <Fig>{fmtDate(r.last_seen)}</Fig>
      </span>
      <span>
        <Tag tone="ghost" title={t('origin: {origin}', { origin: r.origin })}>
          {live ? t('live') : t('from index')}
        </Tag>
      </span>
    </div>
  );
}
